import React, { useMemo } from 'react';
import { StyleSheet } from 'react-native';
import { createMaterialTopTabNavigator } from '@react-navigation/material-top-tabs';

import { Strategy } from '~/api-services/AIStrategyBuilderService/types';
import { useStyles } from '~/hooks';
import { useTheme } from '~/hooks/useTheme';
import i18n from '~/i18n';
import { FONT_FAMILIES, FONT_SIZE_S } from '~/constants/typography';
import { scaleSize } from '~/constants/scale';
import { Theme } from '~/theme';

import { strategyResultView } from '../../utils/results';
import ChartTab from './ChartTab';
import SetupTab from './SetupTab';
import StatisticsTab from './StatisticsTab';
import TradesTab from './TradesTab';

const Tab = createMaterialTopTabNavigator();

type Props = {
    strategy: Strategy;
};

// Result exploration tabs, styled to match the Markets top tabs.
export default function DetailTabs({ strategy }: Props) {
    const styles = useStyles(stylesGenerator);
    const theme = useTheme();
    const results = useMemo(() => strategyResultView(strategy), [strategy]);

    return (
        <Tab.Navigator
            screenOptions={{
                lazy: true,
                tabBarActiveTintColor: theme.tabIconSelected,
                tabBarInactiveTintColor: theme.tabIconDefault,
                tabBarIndicatorStyle: { backgroundColor: theme.tabIconSelected },
                tabBarLabelStyle: styles.tabLabel,
                tabBarStyle: styles.tabBar,
            }}
        >
            <Tab.Screen name="StrategyStatistics" options={{ title: i18n.t('ai-strategy-tab-statistics') }}>
                {() => <StatisticsTab results={results} />}
            </Tab.Screen>
            <Tab.Screen name="StrategyTrades" options={{ title: i18n.t('ai-strategy-tab-trades') }}>
                {() => <TradesTab results={results} />}
            </Tab.Screen>
            <Tab.Screen name="StrategyChart" options={{ title: i18n.t('ai-strategy-tab-chart') }}>
                {() => <ChartTab results={results} />}
            </Tab.Screen>
            <Tab.Screen name="StrategySetup" options={{ title: i18n.t('ai-strategy-tab-setup') }}>
                {() => <SetupTab strategy={strategy} />}
            </Tab.Screen>
        </Tab.Navigator>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        tabLabel: {
            fontFamily: FONT_FAMILIES.LATO_BOLD,
            fontSize: scaleSize(FONT_SIZE_S),
            textTransform: 'none',
        },
        tabBar: {
            backgroundColor: theme.background,
        },
    });
